import { useId, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useLanguage } from "../../i18n/language-provider";
import { TermsConsentRefusedError, acceptTermsConsent } from "../../services/termsConsentApi";
import { OptivoWordmark } from "../brand/OptivoWordmark";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    TERMS_CONSENT_ITEMS,
    TERMS_EXPRESS_PROVISIONS,
    isValidCompanyId,
    normalizeCompanyId,
    type TermsConsentItem,
} from "./termsConsentItems";

/**
 * The general terms (VOP) accepted, as their clause 2.1 asks at account
 * creation: the company ID of the business the person acts for, that they
 * contract as a business, that they know the terms and their annexes and
 * accept them, and each provision of clause 2.1.4 in a box of its own.
 *
 * The shell shows it in place of everything else to a signed-in person who
 * has not accepted the current wording, before the screen that waits for an
 * administrator's approval, since the server refuses them every other request
 * (`TERMS_NOT_ACCEPTED`). Nothing is ticked for them: a box ticked for
 * somebody is no acceptance.
 */

interface TermsConsentScreenProps {
    /** The wording the person is shown; the server refuses an acceptance of any other. */
    version: string;
    email: string | null;
    /** The company ID of an earlier acceptance, filled in again. */
    previousCompanyId: string | null;
    /** An account an administrator has still to activate is told so before it accepts. */
    pendingApproval?: boolean;
    onAccepted: () => void;
    onSignOut: () => void;
}

type ProvisionKey = (typeof TERMS_EXPRESS_PROVISIONS)[number];

/**
 * The article of the general terms each provision names, and the section the
 * link lands on (`/terms#…`). `TermsConsentScreen.test.tsx` holds each to the
 * terms' own numbering.
 */
export const PROVISION_ARTICLES: Record<ProvisionKey, { article: string; sectionId: string }> = {
    serviceNature: { article: "3", sectionId: "service" },
    pricePush: { article: "6", sectionId: "push" },
    automatedTools: { article: "4.6–4.8", sectionId: "calculation" },
    anonymisedData: { article: "8.3", sectionId: "user-data" },
    knowHowPenalty: { article: "18", sectionId: "know-how" },
    liability: { article: "12", sectionId: "liability" },
};

const DOCUMENT_LINKS = [
    ["documentTerms", "/terms"],
    ["documentSpecification", "/specification"],
    ["documentDpa", "/dpa"],
    ["documentPriceList", "/subscription"],
] as const;

type ConsentForm = { companyId: string } & Record<TermsConsentItem, boolean>;

type Failure = "versionChanged" | "failed" | null;

export function TermsConsentScreen({
    version,
    email,
    previousCompanyId,
    pendingApproval = false,
    onAccepted,
    onSignOut,
}: TermsConsentScreenProps) {
    const { t } = useLanguage();
    const copy = t.termsConsent;
    const idPrefix = useId();
    const [failure, setFailure] = useState<Failure>(null);

    const schema = z
        .object({
            companyId: z.string().refine((value) => isValidCompanyId(normalizeCompanyId(value)), copy.companyIdInvalid),
            business: z.boolean(),
            terms: z.boolean(),
            serviceNature: z.boolean(),
            pricePush: z.boolean(),
            automatedTools: z.boolean(),
            anonymisedData: z.boolean(),
            knowHowPenalty: z.boolean(),
            liability: z.boolean(),
        })
        .superRefine((values, ctx) => {
            if (TERMS_CONSENT_ITEMS.some((key) => values[key] !== true)) {
                ctx.addIssue({ code: "custom", path: ["items"], message: copy.allRequired });
            }
        });

    const form = useForm<ConsentForm>({
        resolver: zodResolver(schema),
        defaultValues: {
            companyId: previousCompanyId ?? "",
            ...Object.fromEntries(TERMS_CONSENT_ITEMS.map((key) => [key, false])),
        } as ConsentForm,
    });
    const { errors, isSubmitting } = form.formState;
    const itemsError = (errors as Record<string, { message?: string } | undefined>).items?.message;

    const onSubmit = form.handleSubmit(async (values) => {
        setFailure(null);
        try {
            await acceptTermsConsent({
                version,
                companyId: normalizeCompanyId(values.companyId),
                accepted: [...TERMS_CONSENT_ITEMS],
            });
            onAccepted();
        } catch (err) {
            setFailure(err instanceof TermsConsentRefusedError && err.code === "TERMS_VERSION_CHANGED" ? "versionChanged" : "failed");
        }
    });

    const box = (key: TermsConsentItem, label: string) => {
        const id = `${idPrefix}-${key}`;
        return (
            <div className="flex items-start gap-3">
                <Controller
                    control={form.control}
                    name={key}
                    render={({ field }) => (
                        <Checkbox
                            id={id}
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                            onBlur={field.onBlur}
                            className="mt-0.5"
                        />
                    )}
                />
                <Label htmlFor={id} className="block text-sm leading-relaxed font-normal select-text">
                    {label}
                </Label>
            </div>
        );
    };

    const companyIdId = `${idPrefix}-company`;
    const companyIdHintId = `${companyIdId}-hint`;
    const companyIdError = errors.companyId?.message;

    return (
        <div className="h-dvh overflow-y-auto overflow-x-hidden bg-background">
            <header className="border-b">
                <div className="mx-auto flex h-16 max-w-2xl items-center justify-between px-4 sm:px-6">
                    <OptivoWordmark className="h-6 text-primary dark:text-foreground" />
                    <Button variant="ghost" className="h-10 sm:h-9" onClick={onSignOut}>
                        {copy.signOut}
                    </Button>
                </div>
            </header>

            <main className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6 sm:py-10">
                <h1 className="text-2xl font-semibold tracking-tight">{copy.title}</h1>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{copy.intro}</p>
                {pendingApproval && <p className="mt-3 text-sm leading-relaxed">{copy.pendingActivation}</p>}
                {email && (
                    <p className="mt-2 text-sm break-words text-muted-foreground">
                        {copy.signedInAs.replace("{email}", email)}
                    </p>
                )}

                <form onSubmit={onSubmit} noValidate className="mt-8 space-y-8 rounded-lg border bg-card p-4 sm:p-6">
                    <div className="space-y-2">
                        <Label htmlFor={companyIdId}>{copy.companyIdLabel}</Label>
                        <Input
                            id={companyIdId}
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={12}
                            className="max-w-48 font-mono"
                            aria-invalid={companyIdError ? true : undefined}
                            aria-describedby={companyIdHintId}
                            {...form.register("companyId")}
                        />
                        <p id={companyIdHintId} className="text-sm text-muted-foreground">
                            {companyIdError ?? (previousCompanyId ? copy.companyIdPrefilled : copy.companyIdHint)}
                        </p>
                    </div>

                    <div className="space-y-4">
                        {box("business", copy.business)}
                        {box("terms", copy.terms)}
                        <div className="pl-7 text-sm">
                            <p className="text-sm text-muted-foreground">{copy.documentsLabel}</p>
                            <ul className="mt-2 space-y-1">
                                {DOCUMENT_LINKS.map(([key, href]) => (
                                    <li key={key}>
                                        <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary underline underline-offset-2"
                                        >
                                            {copy[key]}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <fieldset className="space-y-4">
                        <legend className="mb-4 text-sm font-medium">{copy.expressHeading}</legend>
                        {TERMS_EXPRESS_PROVISIONS.map((key) => {
                            const { article, sectionId } = PROVISION_ARTICLES[key];
                            return (
                                <div key={key} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                                    <div className="min-w-0 flex-1">{box(key, copy[key])}</div>
                                    <a
                                        href={`/terms#${sectionId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="pl-7 text-sm whitespace-nowrap text-primary underline underline-offset-2 sm:pl-0"
                                    >
                                        {copy.articleLink.replace("{article}", article)}
                                    </a>
                                </div>
                            );
                        })}
                    </fieldset>

                    <div className="space-y-3">
                        {itemsError && <p className="text-sm text-destructive">{itemsError}</p>}
                        {failure && (
                            <div role="alert" className="space-y-2 text-sm text-destructive">
                                <p>{failure === "versionChanged" ? copy.versionChanged : copy.failed}</p>
                                {failure === "versionChanged" && (
                                    <Button type="button" variant="outline" onClick={() => window.location.reload()}>
                                        {copy.reload}
                                    </Button>
                                )}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="h-auto min-h-10 w-full whitespace-normal sm:min-h-9 sm:w-auto"
                        >
                            {isSubmitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
                            {isSubmitting ? copy.submitting : copy.submit}
                        </Button>
                    </div>
                </form>
            </main>
        </div>
    );
}
